import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Pencil, Plus, Search, X } from 'lucide-react';
import { DropdownMenu as DM } from 'radix-ui';
import { toast } from 'sonner';
import { callingCodeForCountry } from '@shared/calling-codes';
import { useEmployees, useWorkspace } from '@/app/workspace';
import { invalidateTasks, qk } from '@/app/query';
import { api } from '@/lib/api';
import { TaskChangedError, type TaskInput } from '@/lib/api/tasks';
import { normalizeInternationalPhone } from '@/lib/phone';
import { useDebounce } from '@/lib/use-debounce';
import { useDraftGuard } from '@/lib/use-draft-guard';
import { cn, errorMessage } from '@/lib/utils';
import {
  TASK_TYPES,
  clockAt,
  dateChoices,
  dueLabel,
  isOverdue,
  reminderChoices,
  reminderFields,
  reminderLabel,
  reminderValue,
  shownType,
  taskTypeLabel,
  timeChoicesFor,
  type ReminderValue,
  type TaskType,
} from '@/lib/tasks';
import type { Employee, Task } from '@/lib/types';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/form';
import { Avatar } from '@/components/ui/misc';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/overlay';
import { Field, Modal, Select, Status } from '@/components/ui/system';

/** What a new task starts with when it is added from a contact or a filtered list. */
export interface TaskPrefill {
  employeeId?: number | null;
  contact?: TaskContact | null;
}

interface TaskContact {
  phone: string;
  name: string | null;
  label: string | null;
}

/** A row of mutually exclusive choices, styled like the filter chips. */
export function ChoiceChips({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="chips choice-chips" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ChoiceField({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="form-field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </div>
  );
}

/** Where the task goes, and what reaching the phone depends on. */
export function deliveryHint(employee: Employee | undefined): string | null {
  if (!employee) return null;
  if (!employee.token)
    return `${employee.name} hasn’t linked the app yet. The task reaches ${employee.name}’s phone once the app is linked.`;
  if (!employee.fcmToken)
    return `${employee.name}’s phone isn’t connected right now. The task shows up once the app is open and signed in.`;
  return `It shows up in the CallSync app on ${employee.name}’s phone.`;
}

// ── Due date, time and reminder ───────────────────────────────────────────

function DueFields({
  dueDate,
  dueTime,
  reminder,
  onChange,
}: {
  dueDate: string;
  dueTime: string;
  reminder?: ReminderValue;
  onChange: (patch: { dueDate?: string; dueTime?: string; reminder?: ReminderValue }) => void;
}) {
  const clock = clockAt();
  const days = dateChoices(clock);
  const times = timeChoicesFor(dueDate || null, clock);
  const [pickingDate, setPickingDate] = useState(!!dueDate && !days.some((d) => d.value === dueDate));
  const [customTime, setCustomTime] = useState(!!dueTime && !times.some((t) => t.value === dueTime));
  const dayValue = !dueDate ? 'none' : pickingDate ? 'pick' : dueDate;
  const timeValue = !dueTime ? 'none' : customTime ? 'custom' : dueTime;
  return (
    <>
      <ChoiceField label="Due">
        <ChoiceChips
          label="Due date"
          value={dayValue}
          options={[...days, { value: 'pick', label: 'Pick date' }, { value: 'none', label: 'No date' }]}
          onChange={(value) => {
            if (value === 'none') {
              setPickingDate(false);
              onChange({ dueDate: '', dueTime: '' });
            } else if (value === 'pick') {
              setPickingDate(true);
              onChange({ dueDate: dueDate || days[1].value });
            } else {
              setPickingDate(false);
              onChange({ dueDate: value });
            }
          }}
        />
        {pickingDate && dueDate && (
          <Input
            type="date"
            aria-label="Due date"
            className="task-date-input"
            value={dueDate}
            onChange={(e) => e.target.value && onChange({ dueDate: e.target.value })}
          />
        )}
      </ChoiceField>
      {dueDate && (
        <ChoiceField label="Time">
          <ChoiceChips
            label="Due time"
            value={timeValue}
            options={[
              ...times,
              { value: 'custom', label: 'Custom' },
              { value: 'none', label: 'No time' },
            ]}
            onChange={(value) => {
              if (value === 'none') {
                setCustomTime(false);
                onChange({ dueTime: '' });
              } else if (value === 'custom') {
                setCustomTime(true);
                onChange({ dueTime: dueTime || '10:00' });
              } else {
                setCustomTime(false);
                onChange({ dueTime: value });
              }
            }}
          />
          {customTime && dueTime && (
            <Input
              type="time"
              aria-label="Due time"
              className="task-date-input"
              value={dueTime}
              onChange={(e) => e.target.value && onChange({ dueTime: e.target.value })}
            />
          )}
        </ChoiceField>
      )}
      {reminder !== undefined && (
        <ChoiceField
          label="Reminder"
          hint={dueDate && dueTime ? 'The phone rings for the reminder.' : 'Set a due time to add a reminder.'}
        >
          {dueDate && dueTime ? (
            <ChoiceChips
              label="Reminder"
              value={reminder}
              options={reminderChoices(reminder)}
              onChange={(value) => onChange({ reminder: value })}
            />
          ) : null}
        </ChoiceField>
      )}
    </>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────

function ContactPicker({ value, onChange }: { value: TaskContact | null; onChange: (c: TaskContact | null) => void }) {
  const { orgId, org } = useWorkspace();
  const [text, setText] = useState('');
  const search = useDebounce(text.trim());
  const results = useQuery({
    queryKey: ['taskContactSearch', orgId, search],
    queryFn: ({ signal }) =>
      api.contacts.listContacts(orgId, { range: { preset: 'all' }, tab: 'all', search }, 1, 6, undefined, signal),
    enabled: !value && search.length >= 2,
    staleTime: 30_000,
  });
  // A number nobody has called from yet: written in full, or with the workspace's country code.
  const typed = useMemo(() => {
    const raw = text.trim();
    if (!raw || /[a-z]/i.test(raw)) return null;
    if (raw.startsWith('+')) return normalizeInternationalPhone(raw);
    const code = callingCodeForCountry(org.billingAddress?.country) ?? '+91';
    return normalizeInternationalPhone(`${code}${raw.replace(/\D/g, '').replace(/^0+/, '')}`);
  }, [text, org.billingAddress?.country]);
  if (value)
    return (
      <div className="task-contact-chosen">
        <Avatar name={value.name} seed={value.phone} size="sm" />
        <span className="task-contact-text">
          <strong>{value.name || value.phone}</strong>
          <span className="subline">{value.name ? value.phone : 'No name saved'}</span>
        </span>
        <Button variant="ghost" size="icon-sm" aria-label="Remove contact" onClick={() => onChange(null)}>
          <X />
        </Button>
      </div>
    );
  const rows = results.data?.rows ?? [];
  const exact = typed && rows.some((c) => c.phone === typed);
  return (
    <div className="task-contact-picker">
      <div className="search-field">
        <Search size={16} />
        <Input
          aria-label="Search contacts or type a number"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Search contacts or type a number"
          autoComplete="off"
        />
      </div>
      {(rows.length > 0 || (typed && !exact) || (search.length >= 2 && results.isFetching)) && (
        <ul className="task-contact-results" aria-label="Matching contacts">
          {rows.map((c) => (
            <li key={c.phone}>
              <button
                type="button"
                onClick={() => {
                  onChange({ phone: c.phone, name: c.name, label: c.labels[0] ?? null });
                  setText('');
                }}
              >
                <Avatar name={c.name} seed={c.phone} size="sm" />
                <span className="min-w-0">
                  <strong>{c.name || c.phone}</strong>
                  <span className="subline">{c.name ? c.phone : 'No name saved'}</span>
                </span>
              </button>
            </li>
          ))}
          {typed && !exact && (
            <li>
              <button
                type="button"
                onClick={() => {
                  onChange({ phone: typed, name: null, label: null });
                  setText('');
                }}
              >
                <Avatar name={null} seed={typed} size="sm" />
                <span className="min-w-0">
                  <strong>Use {typed}</strong>
                  <span className="subline">Not in your contacts yet</span>
                </span>
              </button>
            </li>
          )}
          {!rows.length && !typed && results.isFetching && <li className="task-contact-empty">Searching…</li>}
        </ul>
      )}
      {search.length >= 2 && !results.isFetching && !rows.length && !typed && (
        <small className="text-fg-muted">No contacts match. Type a full number to use it.</small>
      )}
    </div>
  );
}

// ── Add and edit ──────────────────────────────────────────────────────────

interface Draft {
  type: TaskType;
  description: string;
  employeeId: string;
  contact: TaskContact | null;
  dueDate: string;
  dueTime: string;
  reminder: ReminderValue;
}

function draftOf(task: Task | null, prefill: TaskPrefill | undefined, employees: Employee[]): Draft {
  if (task)
    return {
      type: task.type,
      description: task.description ?? '',
      employeeId: task.assignedEmployeeId === null ? '' : String(task.assignedEmployeeId),
      contact: task.contactPhone
        ? { phone: task.contactPhone, name: task.contactName, label: task.contactLabel }
        : null,
      dueDate: task.dueDate ?? '',
      dueTime: task.dueTime ?? '',
      reminder: reminderValue(task.showReminder, task.reminderMinutesBefore),
    };
  const preferred = prefill?.employeeId ?? lastAssignee();
  const employee = employees.find((e) => e.id === preferred) ?? (employees.length === 1 ? employees[0] : undefined);
  return {
    type: 'FOLLOW_UP',
    description: '',
    employeeId: employee ? String(employee.id) : '',
    contact: prefill?.contact ?? null,
    dueDate: clockAt().today,
    dueTime: '',
    reminder: '0',
  };
}

function inputOf(draft: Draft): TaskInput {
  const dueDate = draft.dueDate || null;
  const dueTime = dueDate ? draft.dueTime || null : null;
  return {
    type: draft.type,
    description: draft.description.trim() || null,
    assignedEmployeeId: draft.employeeId ? Number(draft.employeeId) : null,
    contactPhone: draft.contact?.phone ?? null,
    contactName: draft.contact?.name ?? null,
    contactLabel: draft.contact?.label ?? null,
    dueDate,
    dueTime,
    ...reminderFields(draft.reminder, dueDate, dueTime),
  };
}

/** Only what changed, so an edit never overwrites a field the employee changed meanwhile. */
function patchOf(task: Task, draft: Draft): Partial<TaskInput> {
  const next = inputOf(draft);
  const current: TaskInput = {
    type: task.type,
    description: task.description,
    assignedEmployeeId: task.assignedEmployeeId,
    contactPhone: task.contactPhone,
    contactName: task.contactName,
    contactLabel: task.contactLabel,
    dueDate: task.dueDate,
    dueTime: task.dueTime,
    showReminder: task.showReminder,
    reminderMinutesBefore: task.showReminder ? task.reminderMinutesBefore : 0,
  };
  const patch: Partial<TaskInput> = {};
  for (const key of Object.keys(next) as (keyof TaskInput)[]) {
    if (next[key] !== current[key]) (patch as Record<string, unknown>)[key] = next[key];
  }
  // The contact name and label follow the number; a renamed contact is not a change.
  if (patch.contactPhone === undefined) {
    delete patch.contactName;
    delete patch.contactLabel;
  }
  return patch;
}

const ASSIGNEE_KEY = 'cs_task_last_assignee';
function lastAssignee(): number | null {
  try {
    const value = Number(localStorage.getItem(ASSIGNEE_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}
function rememberAssignee(id: number | null) {
  try {
    if (id) localStorage.setItem(ASSIGNEE_KEY, String(id));
  } catch {
    /* optional browser storage */
  }
}

async function refreshAfterTaskChange(orgId: string) {
  await invalidateTasks(orgId);
}

/** Which task a dialog shows; a new `session` starts it afresh. */
export interface TaskEditor {
  open: boolean;
  session: number;
  /** Null to add a task. */
  task: Task | null;
  prefill?: TaskPrefill;
}
export const closedEditor: TaskEditor = { open: false, session: 0, task: null };

/**
 * Stays mounted and opens with `editor.open`, like the other dialogs: an overlay that mounts
 * already open loses its Back-button history entry in development's double effects.
 */
export function TaskDialog({ editor, onClose }: { editor: TaskEditor; onClose: () => void }) {
  const { open, task, prefill } = editor;
  const { orgId } = useWorkspace();
  const people = useEmployees();
  const employees = useMemo(() => people.data ?? [], [people.data]);
  const [session, setSession] = useState(editor.session);
  const [base, setBase] = useState<Task | null>(task);
  const [draft, setDraft] = useState<Draft>(() => draftOf(task, prefill, employees));
  const [initial, setInitial] = useState<Draft>(draft);
  const [changedNotice, setChangedNotice] = useState(false);
  if (open && editor.session !== session) {
    const next = draftOf(task, prefill, employees);
    setSession(editor.session);
    setBase(task);
    setDraft(next);
    setInitial(next);
    setChangedNotice(false);
  }
  // The employee list may arrive after the dialog opens.
  useEffect(() => {
    if (open && !base && !draft.employeeId && employees.length) {
      const next = draftOf(null, prefill, employees);
      if (next.employeeId) setDraft((d) => ({ ...d, employeeId: next.employeeId }));
    }
  }, [open, employees, base, prefill, draft.employeeId]);
  const update = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const save = useMutation({
    mutationFn: async () => {
      if (!base) return api.tasks.createTask(orgId, inputOf(draft));
      return api.tasks.updateTask(orgId, base, patchOf(base, draft));
    },
    onSuccess: async (saved) => {
      rememberAssignee(saved.assignedEmployeeId);
      await refreshAfterTaskChange(orgId);
      const employee = employees.find((e) => e.id === saved.assignedEmployeeId);
      toast.success(
        base
          ? 'Task saved'
          : employee
            ? employee.fcmToken
              ? `Task sent to ${employee.name}’s phone`
              : `Task added. ${employee.name}’s phone isn’t connected, so it shows up once the app is signed in.`
            : 'Task added',
      );
      onClose();
    },
    onError: (error) => {
      if (error instanceof TaskChangedError) {
        if (!error.task) {
          toast.error('This task was deleted.');
          void refreshAfterTaskChange(orgId);
          onClose();
          return;
        }
        const latest = draftOf(error.task, undefined, employees);
        setBase(error.task);
        setDraft(latest);
        setInitial(latest);
        setChangedNotice(true);
        void refreshAfterTaskChange(orgId);
        return;
      }
      toast.error(errorMessage(error));
    },
  });
  const guard = useDraftGuard(open && dirty, save.isPending);
  const close = () => guard(onClose);
  const assignee = employees.find((e) => String(e.id) === draft.employeeId);
  const currentAway =
    base?.assignedEmployeeId != null && !employees.some((e) => e.id === base.assignedEmployeeId)
      ? {
          value: String(base.assignedEmployeeId),
          label: `${base.assignedEmployeeName ?? 'Removed member'}${base.assignedEmployeeArchived ? ' (archived)' : ''}`,
        }
      : null;
  const missingAssignee = !base && !draft.employeeId;
  // Added from a contact: the task is for that contact, so there is nothing to pick.
  const forContact = !base && prefill?.contact ? prefill.contact : null;
  const typeValue = shownType(draft.type);
  const formId = base ? `task-edit-${base.id}` : 'task-new';
  return (
    <Modal
      open={open}
      onClose={close}
      busy={save.isPending}
      title={base ? 'Edit task' : 'Add a task'}
      description={
        base
          ? base.createdBy === 'employee'
            ? `Added on ${base.createdByEmployeeName ?? 'a team member'}’s phone. Changes reach their phone.`
            : 'Changes reach the assigned person’s phone.'
          : forContact
            ? `Task for ${forContact.name || forContact.phone}`
            : 'Give a team member a follow-up. It shows up in the CallSync app on their phone.'
      }
      footer={
        <>
          <Button
            variant="secondary"
            onClick={close}
            disabled={save.isPending}
            disabledReason="Please wait while the task saves."
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            loading={save.isPending}
            disabled={missingAssignee || (!!base && !dirty)}
            disabledReason={missingAssignee ? 'Choose who the task is for.' : 'Nothing has changed yet.'}
          >
            {base ? 'Save' : 'Add task'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="form-stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!missingAssignee && !save.isPending) save.mutate();
        }}
      >
        {changedNotice && (
          <p className="task-changed-notice" role="status">
            This task changed on the phone after you opened it. These are the latest details; make your change again.
          </p>
        )}
        <ChoiceField label="Type">
          <ChoiceChips
            label="Task type"
            value={typeValue}
            options={TASK_TYPES.map((type) => ({ value: type, label: taskTypeLabel(type) }))}
            // An older type (Call, Email) stays as it is until another type is picked.
            onChange={(type) => type !== typeValue && update({ type: type as TaskType })}
          />
        </ChoiceField>
        <Field label="Details (optional)">
          <Textarea
            value={draft.description}
            maxLength={5000}
            rows={3}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="e.g. Share the price list and ask about a site visit"
          />
        </Field>
        <ChoiceField label="Assign to" hint={deliveryHint(assignee) ?? (draft.employeeId ? undefined : 'Not on anyone’s phone yet.')}>
          <Select
            label="Assign to"
            value={draft.employeeId}
            disabled={people.isPending}
            onChange={(employeeId) => update({ employeeId })}
            options={[
              ...(base
                ? [{ value: '', label: 'Not assigned' }]
                : draft.employeeId
                  ? []
                  : [{ value: '', label: people.isPending ? 'Loading team…' : 'Choose a team member' }]),
              ...employees.map((e) => ({ value: String(e.id), label: e.name })),
              ...(currentAway ? [currentAway] : []),
            ]}
          />
        </ChoiceField>
        {!forContact && (
          <ChoiceField label="Contact (optional)">
            <ContactPicker value={draft.contact} onChange={(contact) => update({ contact })} />
            {draft.contact && !draft.contact.name && (
              <Input
                aria-label="Contact name"
                placeholder="Name (optional)"
                maxLength={200}
                value={draft.contact.name ?? ''}
                onChange={(e) => update({ contact: { ...draft.contact!, name: e.target.value || null } })}
              />
            )}
          </ChoiceField>
        )}
        <DueFields
          key={`${session}:${base?.updatedAt ?? 'new'}`}
          dueDate={draft.dueDate}
          dueTime={draft.dueTime}
          reminder={draft.reminder}
          // A first due time rings on time, as in the app.
          onChange={(patch) =>
            update(patch.dueTime && !draft.dueTime && draft.reminder === 'none' ? { ...patch, reminder: '0' } : patch)
          }
        />
      </form>
    </Modal>
  );
}

/**
 * A new due day and time; a reminder keeps its lead time, and goes once there is no time. A
 * task getting its first time rings on time, as in the app.
 */
function reschedulePatch(task: Task, dueDate: string, dueTime: string): Partial<TaskInput> {
  const date = dueDate || null;
  const time = date ? dueTime || null : null;
  const patch: Partial<TaskInput> = { dueDate: date, dueTime: time };
  if (task.showReminder && !time) Object.assign(patch, { showReminder: false, reminderMinutesBefore: 0 });
  else if (!task.dueTime && time && !task.showReminder) Object.assign(patch, { showReminder: true, reminderMinutesBefore: 0 });
  return patch;
}
const sameDue = (task: Task, dueDate: string, dueTime: string) =>
  (dueDate || null) === task.dueDate && ((dueDate && dueTime) || null) === task.dueTime;

/** Only the due day and time; a reminder keeps its lead time. Mounted like TaskDialog. */
export function RescheduleDialog({ editor, onClose }: { editor: TaskEditor; onClose: () => void }) {
  const { orgId } = useWorkspace();
  const { open } = editor;
  const [session, setSession] = useState(editor.session);
  const [task, setTask] = useState(editor.task);
  const [dueDate, setDueDate] = useState(editor.task?.dueDate ?? clockAt().today);
  const [dueTime, setDueTime] = useState(editor.task?.dueTime ?? '');
  if (open && editor.session !== session) {
    setSession(editor.session);
    setTask(editor.task);
    setDueDate(editor.task?.dueDate ?? clockAt().today);
    setDueTime(editor.task?.dueTime ?? '');
  }
  const save = useMutation({
    mutationFn: () => {
      if (!task) throw new Error('Choose a task to reschedule.');
      return api.tasks.updateTask(orgId, task, reschedulePatch(task, dueDate, dueTime));
    },
    onSuccess: async () => {
      await refreshAfterTaskChange(orgId);
      toast.success('Task rescheduled');
      onClose();
    },
    onError: async (error) => {
      await refreshAfterTaskChange(orgId);
      toast.error(errorMessage(error));
      if (error instanceof TaskChangedError) onClose();
    },
  });
  const unchanged = !task || sameDue(task, dueDate, dueTime);
  return (
    <Modal
      open={open && !!task}
      onClose={onClose}
      busy={save.isPending}
      title="Reschedule"
      description={task ? (task.description ?? taskTypeLabel(task.type)) : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={unchanged}
            disabledReason="Choose a new day or time."
            onClick={() => save.mutate()}
          >
            Reschedule
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <DueFields
          key={session}
          dueDate={dueDate}
          dueTime={dueTime}
          onChange={(patch) => {
            if (patch.dueDate !== undefined) setDueDate(patch.dueDate);
            if (patch.dueTime !== undefined) setDueTime(patch.dueTime);
          }}
        />
      </div>
    </Modal>
  );
}

// ── Row actions ───────────────────────────────────────────────────────────

/** Done, reopen and delete, shared by the Tasks page and the contact panel. */
export function useTaskActions() {
  const { orgId } = useWorkspace();
  const confirm = useConfirm();
  const done = useMutation({
    mutationFn: ({ task, value }: { task: Task; value: boolean }) => api.tasks.markTaskDone(orgId, task, value),
    onSuccess: async (saved, { value }) => {
      await refreshAfterTaskChange(orgId);
      toast.success(value ? 'Marked done' : 'Task reopened', {
        action: {
          label: 'Undo',
          onClick: () =>
            void api.tasks
              .markTaskDone(orgId, saved, !value)
              .then(() => refreshAfterTaskChange(orgId))
              .catch((e) => toast.error(errorMessage(e))),
        },
      });
    },
    onError: async (error) => {
      await refreshAfterTaskChange(orgId);
      toast.error(errorMessage(error));
    },
  });
  const remove = (task: Task) =>
    confirm({
      title: 'Delete this task?',
      description: task.assignedEmployeeName
        ? `It’s also removed from ${task.assignedEmployeeName}’s phone. This can’t be undone.`
        : 'This can’t be undone.',
      confirmLabel: 'Delete task',
      tone: 'danger',
      action: async () => {
        await api.tasks.deleteTask(orgId, task);
        await refreshAfterTaskChange(orgId);
      },
      successMessage: 'Task deleted',
    });
  return { done, remove };
}

/** The round check that marks a task done. */
export function DoneToggle({
  task,
  onToggle,
  disabled,
}: {
  task: Task;
  onToggle: (value: boolean) => void;
  disabled?: boolean;
}) {
  const isDone = task.status === 'COMPLETED';
  if (task.status === 'CANCELLED') return <span className="task-done-toggle" aria-hidden />;
  return (
    <button
      type="button"
      className={cn('task-done-toggle', isDone && 'is-done')}
      aria-pressed={isDone}
      aria-label={isDone ? 'Reopen task' : 'Mark done'}
      title={isDone ? 'Reopen task' : 'Mark done'}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(!isDone);
      }}
    >
      <Check size={12} strokeWidth={3} />
    </button>
  );
}


// ── Editing in the table ──────────────────────────────────────────────────

/** One field saved from a table cell, and what to say once it is saved. */
export function useTaskField() {
  const { orgId } = useWorkspace();
  return useMutation({
    mutationFn: ({ task, patch }: { task: Task; patch: Partial<TaskInput>; message: string }) =>
      api.tasks.updateTaskField(orgId, task, patch),
    onSuccess: async (_saved, { message }) => {
      await refreshAfterTaskChange(orgId);
      toast.success(message);
    },
    onError: async (error) => {
      await refreshAfterTaskChange(orgId);
      toast.error(errorMessage(error));
    },
  });
}
type TaskField = ReturnType<typeof useTaskField>;
const savingRow = (field: TaskField, task: Task) => field.isPending && field.variables?.task.id === task.id;

/** A cell that opens a short list and saves the choice straight away. */
function PickCell({
  label,
  value,
  options,
  onPick,
  disabled,
  children,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onPick: (value: string) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button type="button" className="table-edit-pick" title={`Change ${label.toLowerCase()}`}>
          {children}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" aria-label={label}>
        <DM.RadioGroup value={value} onValueChange={(next) => next !== value && onPick(next)}>
          {options.map((option) => (
            <DM.RadioItem key={option.value} value={option.value} className="more-filter-option">
              <span className="more-filter-check">
                <DM.ItemIndicator>
                  <Check size={14} />
                </DM.ItemIndicator>
              </span>
              <span>{option.label}</span>
            </DM.RadioItem>
          ))}
        </DM.RadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TypeCell({ task, field }: { task: Task; field: TaskField }) {
  return (
    <PickCell
      label="Task type"
      value={shownType(task.type)}
      disabled={savingRow(field, task)}
      options={TASK_TYPES.map((type) => ({ value: type, label: taskTypeLabel(type) }))}
      onPick={(type) =>
        field.mutate({ task, patch: { type: type as TaskType }, message: `Changed to ${taskTypeLabel(type)}` })
      }
    >
      {taskTypeLabel(task.type)}
    </PickCell>
  );
}

const UNASSIGNED = 'none';

export function AssigneeCell({ task, field }: { task: Task; field: TaskField }) {
  const people = useEmployees();
  const employees = people.data ?? [];
  const current = task.assignedEmployeeId === null ? UNASSIGNED : String(task.assignedEmployeeId);
  const name = `${task.assignedEmployeeName ?? 'Removed member'}${task.assignedEmployeeArchived ? ' (archived)' : ''}`;
  const away = task.assignedEmployeeId !== null && !employees.some((e) => e.id === task.assignedEmployeeId);
  return (
    <PickCell
      label="Assign to"
      value={current}
      disabled={people.isPending || savingRow(field, task)}
      options={[
        ...employees.map((e) => ({ value: String(e.id), label: e.name })),
        ...(away ? [{ value: current, label: name }] : []),
        { value: UNASSIGNED, label: 'Not assigned' },
      ]}
      onPick={(value) => {
        const employee = employees.find((e) => String(e.id) === value);
        field.mutate({
          task,
          patch: { assignedEmployeeId: employee ? employee.id : null },
          message: !employee
            ? 'Task unassigned'
            : employee.fcmToken
              ? `Task sent to ${employee.name}’s phone`
              : `Assigned to ${employee.name}. Their phone isn’t connected, so it shows up once the app is signed in.`,
        });
      }}
    >
      {task.assignedEmployeeId === null ? <span className="text-fg-subtle">Not assigned</span> : name}
    </PickCell>
  );
}

export function DueCell({ task, field }: { task: Task; field: TaskField }) {
  const clock = clockAt();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(0);
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const label = dueLabel(task.dueDate, task.dueTime, clock);
  const shown = isOverdue(task, clock) ? (
    <Status tone="danger">{label}</Status>
  ) : (
    <span className={cn(!task.dueDate && 'text-fg-subtle')}>{label}</span>
  );
  if (task.status !== 'PENDING') return shown;
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setDueDate(task.dueDate ?? clock.today);
          setDueTime(task.dueTime ?? '');
          setSession((n) => n + 1);
        }
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button type="button" className="table-edit-pick" title="Change due date">
          {shown}
        </button>
      </PopoverTrigger>
      <PopoverContent className="task-cell-popover" aria-label="Change due date">
        <div className="form-stack">
          <DueFields
            key={session}
            dueDate={dueDate}
            dueTime={dueTime}
            onChange={(patch) => {
              if (patch.dueDate !== undefined) setDueDate(patch.dueDate);
              if (patch.dueTime !== undefined) setDueTime(patch.dueTime);
            }}
          />
        </div>
        <div className="task-cell-popover-actions">
          <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={savingRow(field, task)}
            disabled={sameDue(task, dueDate, dueTime)}
            disabledReason="Choose a new day or time."
            onClick={() =>
              field
                .mutateAsync({ task, patch: reschedulePatch(task, dueDate, dueTime), message: 'Task rescheduled' })
                .then(() => setOpen(false), () => undefined)
            }
          >
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ReminderCell({ task, field }: { task: Task; field: TaskField }) {
  const timed = !!task.dueDate && !!task.dueTime;
  const label = timed ? reminderLabel(task.showReminder, task.reminderMinutesBefore) : null;
  if (!timed || task.status !== 'PENDING')
    return label ?? (
      <span className="text-fg-subtle" title={timed ? undefined : 'Set a due time to add a reminder.'}>
        —
      </span>
    );
  const current = reminderValue(task.showReminder, task.reminderMinutesBefore);
  return (
    <PickCell
      label="Reminder"
      value={current}
      disabled={savingRow(field, task)}
      options={reminderChoices(current)}
      onPick={(value) =>
        field.mutate({
          task,
          patch: reminderFields(value, task.dueDate, task.dueTime),
          message: value === 'none' ? 'Reminder turned off' : 'Reminder saved',
        })
      }
    >
      {label ?? <span className="text-fg-subtle">No reminder</span>}
    </PickCell>
  );
}

/** Opens the contact; the pencil (or an empty cell) picks another one. */
export function ContactCell({ task, field, onOpen }: { task: Task; field: TaskField; onOpen: () => void }) {
  const [open, setOpen] = useState(false);
  const pick = (contact: TaskContact | null) =>
    field
      .mutateAsync({
        task,
        patch: {
          contactPhone: contact?.phone ?? null,
          contactName: contact?.name ?? null,
          contactLabel: contact?.label ?? null,
        },
        message: contact ? 'Contact saved' : 'Contact removed',
      })
      .then(() => setOpen(false), () => undefined);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      {task.contactPhone ? (
        <div className="task-contact-cell">
          <button type="button" className="table-person text-left" onClick={onOpen}>
            <Avatar name={task.contactName} seed={task.contactPhone} size="sm" />
            <span>
              <strong>{task.contactName || task.contactPhone}</strong>
              <span className="subline">{task.contactName ? task.contactPhone : 'No name saved'}</span>
            </span>
          </button>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon-xs" aria-label="Change contact" title="Change contact">
              <Pencil />
            </Button>
          </PopoverTrigger>
        </div>
      ) : (
        <PopoverTrigger asChild>
          <button type="button" className="table-edit-pick" title="Add a contact">
            {task.contactName || <span className="text-fg-subtle">Add contact</span>}
          </button>
        </PopoverTrigger>
      )}
      <PopoverContent className="task-cell-popover" aria-label="Choose a contact">
        <ContactPicker value={null} onChange={(contact) => contact && void pick(contact)} />
        {task.contactPhone && (
          <div className="task-cell-popover-actions">
            <Button variant="ghost" size="sm" disabled={savingRow(field, task)} onClick={() => void pick(null)}>
              <X />
              Remove contact
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ── Contact panel ─────────────────────────────────────────────────────────

/** Open tasks for one number, with a shortcut to add one for it. */
export function ContactTasks({ phone, name, label }: { phone: string; name: string | null; label: string | null }) {
  const { orgId } = useWorkspace();
  const [editor, setEditor] = useState<TaskEditor>(closedEditor);
  const { done } = useTaskActions();
  const query = { tab: 'open' as const, phone };
  const tasks = useQuery({
    queryKey: qk.tasks(orgId, 'contact', phone),
    queryFn: ({ signal }) => api.tasks.listTasks(orgId, query, 1, 20, signal),
  });
  const clock = clockAt();
  const rows = tasks.data?.rows ?? [];
  return (
    <section className="contact-section contact-tasks" aria-label="Open tasks">
      <div className="contact-tasks-heading">
        <h3>Open tasks</h3>
        <Button
          variant="secondary"
          size="xs"
          onClick={() =>
            setEditor({ open: true, session: Date.now(), task: null, prefill: { contact: { phone, name, label } } })
          }
        >
          <Plus />
          Add task
        </Button>
      </div>
      {tasks.isPending ? (
        <p>Loading tasks…</p>
      ) : tasks.error ? (
        <p>
          Tasks couldn’t be loaded.{' '}
          <button type="button" className="underline" onClick={() => tasks.refetch()}>
            Try again
          </button>
        </p>
      ) : rows.length ? (
        <ul className="contact-task-list">
          {rows.map((t) => (
            <li key={t.id}>
              <DoneToggle
                task={t}
                disabled={done.isPending && done.variables?.task.id === t.id}
                onToggle={(value) => done.mutate({ task: t, value })}
              />
              <button
                type="button"
                className="contact-task"
                onClick={() => setEditor({ open: true, session: Date.now(), task: t })}
              >
                <strong>{t.description || taskTypeLabel(t.type)}</strong>
                <span className="subline">
                  {isOverdue(t, clock) ? (
                    <span className="text-danger">Overdue · {dueLabel(t.dueDate, t.dueTime, clock)}</span>
                  ) : (
                    dueLabel(t.dueDate, t.dueTime, clock)
                  )}
                  {' · '}
                  {t.assignedEmployeeName ?? 'Not assigned'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p>No open tasks for this contact.</p>
      )}
      <TaskDialog editor={editor} onClose={() => setEditor((e) => ({ ...e, open: false }))} />
    </section>
  );
}
